import React, {useCallback, useMemo, useState, useRef, useEffect, useContext} from 'react';
import clockIco from '../../../assets/icons/interface/clock.svg';
import walkIco from '../../../assets/icons/interface/walk.svg';
import carIco from '../../../assets/icons/interface/car.svg';
import redPlaceIco from '../../../assets/icons/interface/red-place.svg';
import grayPlaceIco from '../../../assets/icons/interface/gray-place.svg';
import closeIco from '../../../assets/icons/close_modal.svg';

import ActionSheet from '../modals/ActionSheet';
import ModalPortal from '../../../ModalPortal';
import SearchForRouteCompomponent from '../modalsContent/SearchComponent';
import RouteRequest from '../../../services/RouteRequest';
import ChooseNavigator from "./ChooseNavigator";

import { useRoute } from "../../../utils/RouteContext";
import {SearchContext} from "../../../utils/SearchContext";

const RouteComponent = ({ roadsData, setSelectedMarker, forceResetOnMount = false }) => {
    const [showSearchSheet, setShowSearchSheet] = useState(false);
    const [showNavigatorSheet, setShowNavigatorSheet] = useState(false);
    const [pointType, setPointType] = useState(null);

    const searchContextValue = useContext(SearchContext);
    const { userPosition } = searchContextValue || {};

    // Берём всё нужное из контекста
    const {
        startPoint, setStartPoint,
        endPoint, setEndPoint,
        routeData, setRouteData,
        embeddedMode, setEmbeddedMode,
        routeLoading, setRouteLoading,
        routeError, setRouteError
    } = useRoute();

    const prevStartPointRef = useRef(null);
    const prevEndPointRef = useRef(null);
    const prevEmbeddedModeRef = useRef(false);

    // Флаг первой инициализации компонента
    const isInitializedRef = useRef(false);

    // Локально храним маршрут (с сервера)
    const [localRouteData, setLocalRouteData] = useState(null);

    // ---------------------------
    // 1) ЭФФЕКТ: Срабатывает один раз при монтировании/размонтировании
    useEffect(() => {
        console.log('Компонент RouteComponent смонтирован');

        // Помечаем, что компонент инициализирован (для других эффектов)
        isInitializedRef.current = true;

        // Запоминаем текущие значения в рефы (необязательно, но обычно полезно)
        prevStartPointRef.current = startPoint;
        prevEndPointRef.current = endPoint;
        prevEmbeddedModeRef.current = embeddedMode;

        return () => {
            console.log('Компонент RouteComponent размонтирован');
            // Помечаем, что размонтированы
            isInitializedRef.current = false;
        };
    }, []);

    useEffect(() => {
        console.log('Компонент RouteComponent смонтирован');

        // Если открыли по forceResetOnMount — сбрасываем embeddedMode сразу
        if (forceResetOnMount) {
            console.log('Поставили embeddedMode = false при новом открытии');
            //setEmbeddedMode(false);
            resetRouteDataOnly();
        }

        isInitializedRef.current = true;
        return () => {
            console.log('Компонент RouteComponent размонтирован');
            isInitializedRef.current = false;
        };
    }, []);


    // ---------------------------
    // 3) ЭФФЕКТ: если userPosition изменилось — обновить точки, которые updatable (если embeddedMode = false)
    useEffect(() => {
        // Если не инициализированы — пропускаем
        if (!isInitializedRef.current) return;

        // Если в режиме embeddedMode, не трогаем координаты
        if (embeddedMode) return;

        // Проверяем startPoint
        if (startPoint?.updatable && Array.isArray(userPosition)) {
            const [newLat, newLng] = userPosition;
            if (startPoint.lat !== newLat || startPoint.lng !== newLng) {
                setStartPoint(prev => ({
                    ...prev,
                    lat: newLat,
                    lng: newLng
                }));
            }
        }

        // Проверяем endPoint
        if (endPoint?.updatable && Array.isArray(userPosition)) {
            const [newLat, newLng] = userPosition;
            if (endPoint.lat !== newLat || endPoint.lng !== newLng) {
                setEndPoint(prev => ({
                    ...prev,
                    lat: newLat,
                    lng: newLng
                }));
            }
        }
    }, [userPosition, embeddedMode, startPoint, endPoint]);

    // ---------------------------
    // 4) ЭФФЕКТ: Отслеживаем изменения точек (startPoint, endPoint, embeddedMode)
    useEffect(() => {
        if (!isInitializedRef.current) return;

        console.log('Изменились точки или режим:', {
            startPoint, endPoint, embeddedMode,
            prevStart: prevStartPointRef.current,
            prevEnd: prevEndPointRef.current,
            prevEmbeddedMode: prevEmbeddedModeRef.current
        });

        if (embeddedMode) {
            const startPointChanged = (
                startPoint && prevStartPointRef.current &&
                (startPoint.lat !== prevStartPointRef.current.lat ||
                    startPoint.lng !== prevStartPointRef.current.lng)
            );

            const endPointChanged = (
                endPoint && prevEndPointRef.current &&
                (endPoint.lat !== prevEndPointRef.current.lat ||
                    endPoint.lng !== prevEndPointRef.current.lng)
            );

            if (startPointChanged || endPointChanged) {
                console.log('Точки изменены в embeddedMode → сбрасываем маршрут');
                resetRouteDataOnly(); // <-- внутри resetRouteDataOnly() есть setEmbeddedMode(false)
            }

            // НЕ делаем setEmbeddedMode(false) тут.
        }

        // Обновляем prev-refs
        prevStartPointRef.current = startPoint;
        prevEndPointRef.current = endPoint;
        prevEmbeddedModeRef.current = embeddedMode;
    }, [startPoint, endPoint, embeddedMode]);


    // Функция для сброса только данных маршрута без сброса точек
    const resetRouteDataOnly = () => {
        console.log('Выполняем resetRouteDataOnly');
        // Убираем флаг embeddedMode
        setEmbeddedMode(false);

        setRouteData(null);
        setLocalRouteData(null);
        setRouteError(null);

        // Не сбрасываем точки - сохраняем startPoint и endPoint!
    };

    // --- Когда маршрут получен, сохраняем его только локально
    const handleRouteReceived = useCallback((geojson) => {
        console.log('Маршрут получен:', geojson);
        setLocalRouteData(geojson);
    }, []);

    const handleRouteLoading = useCallback((loading) => {
        setRouteLoading(loading);
    }, []);

    const handleRouteError = useCallback((error) => {
        setRouteError(error);
    }, []);

    // --- Обработка "Встроенный" из ChooseNavigator
    const handleUseEmbeddedNavigator = () => {
        setShowNavigatorSheet(false);
        // Здесь вместо onClose() переводим компонент в embeddedMode
        setEmbeddedMode(true);

        // При желании уже сейчас можем выдать маршрут в контекст, чтоб карта нарисовала.
        // Но только если localRouteData есть (маршрут с сервера получен).
        if (localRouteData) {
            setRouteData(localRouteData);
        }
    };

    // --- Сброс маршрута: возвращаемся к исходному состоянию
    const handleResetRoute = () => {
        // Убираем флаг, возвращаем выбор
        setEmbeddedMode(false);

        // Удаляем маршрут из контекста (убрать с карты)
        setRouteData(null);

        // Сбрасываем локальные данные
        setLocalRouteData(null);
        setRouteError(null);

        // При желании можно и start/end почистить, если нужно
        //setStartPoint(null);
        setEndPoint(null);
    };

    const closeNavigatorSheet = () => {
        setShowNavigatorSheet(false);
    };

    const openNavigatorSheet = () => {
        setShowNavigatorSheet(true);
    };

    const openSearchSheet = (type) => {
        setPointType(type);
        setShowSearchSheet(true);
    };

    const closeSearchSheet = () => {
        setShowSearchSheet(false);
    };

    const handleSelectRoutePoint = (result, type) => {
        console.log('Выбрали точку:', result, 'Тип:', type);

        if (type === 'start') {
            setStartPoint(result);
        } else if (type === 'end') {
            setEndPoint(result);
        }

        setLocalRouteData(null);
        setRouteError(null);
    };

    const handleClearStart = (e) => {
        e.stopPropagation();
        setStartPoint(null);
        setLocalRouteData(null);
        setRouteError(null);
    };

    const handleClearEnd = (e) => {
        e.stopPropagation();
        setEndPoint(null);
        setLocalRouteData(null);
        setRouteError(null);
    };

    const fromCoords = useMemo(() => {
        if (!startPoint) return null;
        return { lat: startPoint.lat, lng: startPoint.lng };
    }, [startPoint]);

    const toCoords = useMemo(() => {
        if (!endPoint) return null;
        return { lat: endPoint.lat, lng: endPoint.lng };
    }, [endPoint]);

    // Проверяем, есть ли маршрут (без ошибки)
    const isRouteReady = !!localRouteData && !routeError;

    // Форматируем distance/time
    let distanceString = '';
    let timeString = '';
    if (isRouteReady) {
        // distance в метрах, time в минутах
        const distance = localRouteData.properties.distance || 0;
        const time = localRouteData.properties.time || 0;

        const hours = Math.floor(time / 60);
        const minutes = Math.round(time % 60);
        timeString = `${hours} ч. ${minutes} мин.`;

        if (distance < 900) {
            distanceString = `${Math.round(distance)} м`;
        } else {
            distanceString = `${(distance / 1000).toFixed(1)} км`;
        }
    }

    // Подпись на кнопке
    let buttonLabel = 'Выберите точки';
    if (startPoint && endPoint) {
        if (routeLoading) {
            buttonLabel = 'Поиск пути...';
        } else if (routeError) {
            buttonLabel = 'Ошибка маршрута';
        } else if (isRouteReady) {
            buttonLabel = 'В путь';
        } else {
            buttonLabel = 'Выберите точки';
        }
    }

    return (
        <div className="container">
            <div className="container__header">
                <h1>Маршрут</h1>
            </div>
            <div className="container__body">

                {/* NEW: если мы в embeddedMode, показываем один блок.
                    Если нет – стандартный route__wrapper. */}

                {!embeddedMode && (
                    <div className="route__wrapper">
                        <div className="route__select-route">
                            <button className="route__item">
                                <img src={walkIco} alt="Walk Icon" width={13} height={25}/>
                                <text className="route__item-text">пешком</text>
                            </button>
                            <button className="route__item route__item--unselected">
                                <img src={carIco} alt="Car Icon" width={30} height={19}/>
                                <text className="route__item-text">авто</text>
                            </button>
                        </div>

                        {/* "Откуда" */}
                        <button
                            className="route__select-point"
                            onClick={() => openSearchSheet('start')}
                        >
                            <img src={grayPlaceIco} alt="Start Icon"/>
                            <text
                                className={
                                    'route__select-text' + (startPoint ? ' route__select-text--selected' : '')
                                }
                            >
                                {startPoint ? startPoint.name : 'Откуда'}
                            </text>
                            {startPoint && (
                                <img
                                    className="route__select-close"
                                    src={closeIco}
                                    alt="удалить место"
                                    onClick={handleClearStart}
                                />
                            )}
                        </button>

                        {/* "Куда" */}
                        <button
                            className="route__select-point"
                            onClick={() => openSearchSheet('end')}
                        >
                            <img src={redPlaceIco} alt="End Icon"/>
                            <text
                                className={
                                    'route__select-text' + (endPoint ? ' route__select-text--selected' : '')
                                }
                            >
                                {endPoint ? endPoint.name : 'Куда'}
                            </text>
                            {endPoint && (
                                <img
                                    className="route__select-close"
                                    src={closeIco}
                                    alt="удалить место"
                                    onClick={handleClearEnd}
                                />
                            )}
                        </button>

                        <div className="route__footer">
                            {/* Показываем время/дистанцию, если маршрут готов */}
                            {isRouteReady && (
                                <div className="route__footer-time">
                                    <div className="route__footer-time-info">
                                        <img src={clockIco} alt="clock Icon"/>
                                        <text className="route__item-text">{timeString}</text>
                                    </div>
                                    <text className="route__item-text">
                                        {distanceString}
                                    </text>
                                </div>
                            )}

                            <button
                                className={
                                    'route__apply' + (isRouteReady ? ' route__apply--ready' : '')
                                }
                                onClick={() => {
                                    if (isRouteReady) {
                                        // Открываем окно ChooseNavigator, чтоб человек выбрал тип навигатора
                                        openNavigatorSheet();
                                    }
                                }}
                            >
                                {buttonLabel}
                            </button>
                        </div>
                    </div>
                )}


                {/* NEW: Если embeddedMode=true, показываем другой блок с итоговыми данными */}
                {embeddedMode && (
                    <div className="route__wrapper">
                        <button className="route__select-point">
                            <img src={grayPlaceIco} alt="Start Icon"/>
                            <text className='route__select-text'>
                                От: {startPoint?.name}
                            </text>
                        </button>
                        <button className="route__select-point">
                            <img src={redPlaceIco} alt="End Icon"/>
                            <text className='route__select-text'>
                                К: {endPoint?.name}
                            </text>
                        </button>
                        <div className="route__footer">
                            <div className="route__footer-time">
                                <div className="route__footer-time-info">
                                    <img src={clockIco} alt="clock Icon"/>
                                    <text className="route__item-text">{timeString}</text>
                                </div>
                                <text className="route__item-text">
                                    {distanceString}
                                </text>
                            </div>
                            <button
                                className="route__apply route__apply--ready"
                                onClick={handleResetRoute}
                            >
                                Сброс
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal: Выбор навигатора */}
            {showNavigatorSheet && (
                <ModalPortal>
                    <ActionSheet
                        onCloseStart={() => {
                        }}
                        onCloseEnd={closeNavigatorSheet}
                        disableSwipeToDismiss={true}
                        showBackButton={true}
                        onBackButtonClick={closeNavigatorSheet}
                    >
                        <ChooseNavigator
                            onClose={closeNavigatorSheet}
                            startPoint={fromCoords}
                            endPoint={toCoords}
                            onUseEmbedded={handleUseEmbeddedNavigator}
                        />
                    </ActionSheet>
                </ModalPortal>
            )}

            {/* Modal: Поиск (Откуда/Куда) */}
            {showSearchSheet && (
                <ModalPortal>
                    <ActionSheet
                        onCloseStart={() => {}}
                        onCloseEnd={closeSearchSheet}
                        disableSwipeToDismiss={false}
                        showBackButton={true}
                        onBackButtonClick={closeSearchSheet}
                    >
                        <SearchForRouteCompomponent
                            onClose={closeSearchSheet}
                            roadsData={roadsData}
                            setSelectedMarker={setSelectedMarker}
                            pointType={pointType}
                            onSelectRoutePoint={handleSelectRoutePoint}
                            showRouteSelect={true}
                            userPosition={searchContextValue?.userPosition}
                        />
                    </ActionSheet>
                </ModalPortal>
            )}

            {/* Автоматический запрос к серверу при наличии обеих точек */}
            {(fromCoords && toCoords) && (
                <RouteRequest
                    from={fromCoords}
                    to={toCoords}
                    onRouteReceived={handleRouteReceived}
                    onRouteLoading={handleRouteLoading}
                    onRouteError={handleRouteError}
                />
            )}
        </div>
    );
};

export default RouteComponent;
